import { rupees, toPaise } from '../../api/upi';

/** Mandate terms as a person edits them: rupees, not paise. */
export interface TermsForm {
  perTxn: string;
  perDay: string;
  perPeriod: string;
  period: string;
  humanAbove: string;
  merchants: string;
}

export const EMPTY_TERMS: TermsForm = { perTxn: '', perDay: '', perPeriod: '', period: 'P1M', humanAbove: '', merchants: '' };

const PERIODS: [string, string][] = [['P1W', 'Week'], ['P1M', 'Month'], ['P3M', 'Quarter'], ['P1Y', 'Year']];

/** The form for a mandate's existing constraints. */
export function termsFromConstraints(c: Record<string, unknown>): TermsForm {
  const r = (k: string) => (c[k] === undefined ? '' : String(Number(c[k]) / 100));
  return {
    perTxn: r('maxPerTxnPaise'),
    perDay: r('maxPerDayPaise'),
    perPeriod: r('maxPerPeriodPaise'),
    period: typeof c.period === 'string' ? c.period : 'P1M',
    humanAbove: r('requireHumanAbovePaise'),
    merchants: Array.isArray(c.allowedMerchantVPAs) ? (c.allowedMerchantVPAs as string[]).join(', ') : '',
  };
}

/**
 * The constraints object for a form, or an error a person can act on.
 * Amounts become integer paise; an empty field is left out.
 */
export function constraintsFromTerms(f: TermsForm): { constraints?: Record<string, unknown>; error?: string } {
  const c: Record<string, unknown> = { currency: 'INR' };
  const amount = (label: string, text: string, key: string, required = false): string | undefined => {
    if (!text.trim()) return required ? `${label} is required.` : undefined;
    const p = toPaise(text);
    if (p === null) return `${label} must be an amount in rupees with at most two decimals.`;
    c[key] = p;
    return undefined;
  };
  const err =
    amount('The per-payment limit', f.perTxn, 'maxPerTxnPaise', true) ??
    amount('The daily limit', f.perDay, 'maxPerDayPaise') ??
    amount('The period limit', f.perPeriod, 'maxPerPeriodPaise') ??
    amount('The approval threshold', f.humanAbove, 'requireHumanAbovePaise');
  if (err) return { error: err };
  if (c.maxPerPeriodPaise !== undefined) c.period = f.period;
  const vpas = f.merchants.split(',').map((s) => s.trim()).filter(Boolean);
  if (vpas.length) c.allowedMerchantVPAs = vpas;
  return { constraints: c };
}

interface Props {
  value: TermsForm;
  onChange: (f: TermsForm) => void;
  /** The bank's limits, shown as a hint: a mandate may not exceed them. */
  bankLimits?: Record<string, unknown>;
}

const input = 'mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 text-xs';

export default function MandateTermsFields({ value, onChange, bankLimits }: Props) {
  const set = (k: keyof TermsForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => onChange({ ...value, [k]: e.target.value });
  const hint = (k: string) => (bankLimits?.[k] !== undefined ? `Bank limit ${rupees(bankLimits[k])}` : '');
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="block">
          <span className="text-xs font-semibold text-text-muted">Per payment (₹)</span>
          <input id="terms-per-txn" inputMode="decimal" value={value.perTxn} onChange={set('perTxn')} placeholder="1500" className={input} />
          <span className="text-[11px] text-text-faint">{hint('maxPerTxnPaise')}</span>
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-text-muted">Per day (₹)</span>
          <input id="terms-per-day" inputMode="decimal" value={value.perDay} onChange={set('perDay')} placeholder="3000" className={input} />
          <span className="text-[11px] text-text-faint">{hint('maxPerDayPaise')}</span>
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-text-muted">Ask me above (₹)</span>
          <input id="terms-human-above" inputMode="decimal" value={value.humanAbove} onChange={set('humanAbove')} placeholder="1000" className={input} />
          <span className="text-[11px] text-text-faint">Payments above this wait for your approval</span>
        </label>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="block">
          <span className="text-xs font-semibold text-text-muted">Per period (₹)</span>
          <input id="terms-per-period" inputMode="decimal" value={value.perPeriod} onChange={set('perPeriod')} placeholder="20000" className={input} />
          <span className="text-[11px] text-text-faint">{hint('maxPerPeriodPaise')}</span>
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-text-muted">Period</span>
          <select id="terms-period" value={value.period} onChange={set('period')} className={input}>
            {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-text-muted">Merchants (UPI IDs, comma-separated)</span>
          <input id="terms-merchants" value={value.merchants} onChange={set('merchants')} placeholder="freshmart@examplebank" className={input} />
        </label>
      </div>
    </div>
  );
}
